import { Metamodel } from 'app/model/base/model';
import { AzureCosmosDbV2Dataset } from 'app/model/dataset/azure-cosmos-db.v2.dataset';
import { BooleanDataFlowType, IntegerDataFlowType, TimeStampDataFlowType } from '../dataflow-datatype';
import { type DataFlowExpression } from '../dataflowexpression';
import { DatasetFormatType } from './dataflow-dataset-settings';
import { TabularTypeSettings } from './tabular-type-settings';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DataFlowSource } from '../dataflow-source';
import { type IViolation } from 'app/model/base/annotation';
import { type DataFlowSink } from '../dataflow-sink';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';

export enum CosmosDBStoreType {
    Transactional = 'oltp',
    Analytical = 'olap'
}

export enum CosmosDBStartFrom {
    Beginning,
    Timestamp,
    Now
}

@Metamodel.model({
    name: adfModelTypeMap.cosmosSql.dataflowSettings,
    validator: CosmosSqlSettings.validateSettings
})
export class CosmosSqlSettings extends TabularTypeSettings {
    public static validateSettings(settings: CosmosSqlSettings): IViolation[] {
        const violations: IViolation[] = [];
        const transform = settings.transform;
        if (transform instanceof DataFlowSource && transform.isInlineDataset) {
            if (!settings.tableName.expression) {
                violations.push({
                    context: settings.tableName,
                    message: () => sanitizeAndFormatString(stringResources.RequiredTemplate, stringResources.ModelName_Container)
                });
            }
            if (settings.useQuery && !settings.query.expression) {
                violations.push({
                    context: settings.query,
                    message: () => sanitizeAndFormatString(stringResources.RequiredTemplate, stringResources.QueryLabel)
                });
            }
        }
        return violations.length > 0 ? violations : undefined;
    }

    @Metamodel.attribute({
        display: stringResources.DataFlow_BatchSize_Description
    })
    public batchSize: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.PartitionKeyLabel
    })
    public partitionKey: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.ThroughputLabel
    })
    public throughput: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.WriteThroughPutBudgetLabel
    })
    public writeThroughPutBudget: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.DataFlowChangeFeed
    })
    public enableChangeFeed: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.DataFlowChangeFeedStartFromBeginning
    })
    public changeFeedStartFromBeginning: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.DataFlowColumnType_Timestamp
    })
    public changeFeedStartTimestamp: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.SystemColumnsLabel
    })
    public systemColumns: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.ModelName_Preferred_Regions
    })
    public preferredRegions: string[];

    @Metamodel.attribute({
        display: stringResources.ModelName_CaptureIntermediateUpdates
    })
    public captureIntermediateUpdate: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.ModelName_CaptureDeletes
    })
    public captureUserDeletes: DataFlowExpression;

    @Metamodel.attribute({
        display: stringResources.ModelName_CaptureTransactionalStoreTTLs
    })
    public captureTxnTTLDeletes: DataFlowExpression;

    public storeType: CosmosDBStoreType = CosmosDBStoreType.Transactional;
    public startFrom: CosmosDBStartFrom = CosmosDBStartFrom.Beginning;
    public readSchema: boolean = false;

    public override get isDatabaseNameRequired(): boolean {
        return __IS_FABRIC_MDF__;
    }

    public initialize(subFormat: string): void {
        super.initialize(subFormat);
        this.enableChangeFeed = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.changeFeedStartFromBeginning = this.transform.createTemplatableExpression(
            BooleanDataFlowType.TYPE,
            'true'
        );
        this.systemColumns = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.writeThroughPutBudget = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE);
        this.throughput = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE);
        this.partitionKey = this.transform.createTemplatableExpression();
        this.batchSize = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE);
        this.changeFeedStartTimestamp = this.transform.createTemplatableExpression(TimeStampDataFlowType.TYPE);
        this.preferredRegions = [];
        this.captureIntermediateUpdate = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.captureUserDeletes = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.captureTxnTTLDeletes = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.additionalConfigs = [];
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.CosmosDB;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getDatasetType(): string {
        return Metamodel.modelFor(AzureCosmosDbV2Dataset).name;
    }

    public getIconClass(): string {
        return 'i-100x100-AzureCosmosDB';
    }

    public get transform(): DataFlowSource | DataFlowSink {
        return this.owner instanceof DataFlowSource ? (this.owner as DataFlowSource) : (this.owner as DataFlowSink);
    }
}
